/**
 * take-pulse-exit-affordance — a context-aware back button for the Take Pulse
 * assessment flow. Renders either an immediate back action or a confirmation
 * warning modal depending on the current flow context.
 *
 * Behavior contract (from BHPD-5047 Gherkin scenarios):
 *  - Scenario 1: non-timed-not-first → immediate onBack, no modal
 *  - Scenario 2: first-screen → exit warning modal shown
 *  - Scenario 3: exit warning confirm → onConfirmExit("first-screen")
 *  - Scenario 4: exit warning cancel → onCancelExit(), modal dismissed
 *  - Scenario 5: timed-active → interruption warning modal shown
 *  - Scenario 6: interruption confirm → onConfirmExit("timed-active")
 *  - Scenario 7: interruption cancel → onCancelExit(), modal dismissed
 *  - Scenario 8: popstate → same context-dependent behavior as button click
 *
 * Reuses existing BHP design-system components:
 *  - Button (plain) + ChevronLeft SVG for the back affordance (same visual as BackButton)
 *  - ConfirmationModal for the warning dialog
 *  - warning-red.svg for the modal illustration
 */
import React, { useState, useCallback, useEffect, useRef } from "react";
import ChevronLeft from "@material-ui/icons/ChevronLeft";
import { ReactComponent as WarningIcon } from "assets/warning-red.svg";
import { Button, ConfirmationModal, StyledGrid } from "components/simple";
import { color, spacingDefaults } from "style/constants";

/** Determines back button behavior within the Take Pulse flow. */
export type ExitContext =
  | "non-timed-not-first"
  | "first-screen"
  | "timed-active";

export type TakePulseExitAffordanceProps = {
  /** Current flow context determining back button behavior */
  context: ExitContext;
  /** Called when back is allowed without warning (non-timed, not first) */
  onBack: () => void;
  /** Called when user confirms exit from a warning modal */
  onConfirmExit: (context: ExitContext) => void;
  /** Called when user cancels a warning modal */
  onCancelExit: () => void;
  /** Optional label for the back button. Defaults to "Back" */
  label?: string;
};

type WarningType = "exit" | "interruption" | null;

// Warning content by type
const WARNING_CONTENT: Record<
  NonNullable<WarningType>,
  { title: string; message: string; confirmLabel: string; cancelLabel: string }
> = {
  exit: {
    title: "Warning!",
    message:
      "Exiting from the Pulse will lose your progress. Do you wish to proceed?",
    confirmLabel: "Yes",
    cancelLabel: "No"
  },
  interruption: {
    title: "Warning!",
    message:
      "Going back will reset this question and you will lose your progress. Do you wish to proceed?",
    confirmLabel: "Yes",
    cancelLabel: "No"
  }
};

const TakePulseExitAffordance: React.FC<TakePulseExitAffordanceProps> = ({
  context,
  onBack,
  onConfirmExit,
  onCancelExit,
  label = "Back"
}) => {
  const [activeWarning, setActiveWarning] = useState<WarningType>(null);

  // Use refs so the popstate listener always reads the latest values
  // without needing to re-register on every state/prop change.
  const activeWarningRef = useRef<WarningType>(null);
  activeWarningRef.current = activeWarning;

  const contextRef = useRef(context);
  contextRef.current = context;

  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;

  const handleBackAction = useCallback(() => {
    // Guard: if a modal is already visible, ignore additional triggers
    if (activeWarningRef.current !== null) return;

    switch (contextRef.current) {
      case "non-timed-not-first":
        onBackRef.current();
        break;
      case "first-screen":
        setActiveWarning("exit");
        break;
      case "timed-active":
        setActiveWarning("interruption");
        break;
    }
  }, []);

  const handleConfirm = useCallback(() => {
    setActiveWarning(null);
    onConfirmExit(context);
  }, [context, onConfirmExit]);

  const handleCancel = useCallback(() => {
    setActiveWarning(null);
    onCancelExit();
  }, [onCancelExit]);

  // Browser back gesture interception via popstate.
  // Pushes exactly one guard entry on mount and pops it on cleanup.
  useEffect(() => {
    window.history.pushState({ pulseExitGuard: true }, "");

    const onPopState = () => {
      // Guard check BEFORE pushing - if modal is already visible, don't mutate history
      if (activeWarningRef.current !== null) {
        // Re-push to keep the guard entry (user pressed back while modal open)
        window.history.pushState({ pulseExitGuard: true }, "");
        return;
      }
      // Re-push guard entry to intercept the next back press
      window.history.pushState({ pulseExitGuard: true }, "");
      handleBackAction();
    };

    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
      // Pop the guard entry we pushed on mount to balance the history stack
      if (window.history.state?.pulseExitGuard) {
        window.history.back();
      }
    };
  }, [handleBackAction]);

  const warningContent = activeWarning ? WARNING_CONTENT[activeWarning] : null;

  return (
    <>
      <StyledGrid container item data-testid="pulse-exit-back-button">
        <Button
          onClick={handleBackAction}
          plain
          textColor={color.DARKBLUE}
          aria-label={label}
        >
          <ChevronLeft />
          <StyledGrid marginLeft={spacingDefaults.small}>{label}</StyledGrid>
        </Button>
      </StyledGrid>

      {activeWarning !== null && warningContent && (
        <div data-testid="pulse-exit-warning-modal">
          <ConfirmationModal
            icon={<WarningIcon />}
            title={warningContent.title}
            content={warningContent.message}
            confirmText={warningContent.confirmLabel}
            cancelText={warningContent.cancelLabel}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
          />
        </div>
      )}
    </>
  );
};

export default TakePulseExitAffordance;
